import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import LeafletMap from '../../../components/base/Map/LeafletMap';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import {
  getFilterData,
  getPaginationOpts,
  getResponseError,
  responseLogWithNotification,
} from '../../../utils/utils';
import { getVehicleRegistryData } from '../reducer';
import { FullScreenControl } from '../../../components/base/Map/FullScreenControl';
import RectangleDrawer from '../../../components/base/Map/RectangleDrawer';
import { loadVehicleRegistryList, loadVehicleTrips, MAP_EVENT } from '../actions';
import { VEHICLES_REGISTRY_MODULE } from '../../../configs/moduleNames';
import { PAGE_TYPE } from '../../FilterTemplate/components/filter-settings';
import FilterModal from '../../FilterTemplate/components/FilterModal';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import { vehicleMapSearchColumns, vehicleRegistryColumns } from './settings';
import Pagination from '../../../components/base/Pagination/Pagination';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import Loader from '../../../components/base/Loader/Loader';
import VehicleFilterRow from './VehicleFilterRow';
import getDefaultSort from '../../../utils/getDefaultSort';
import PointMarker from '../../../components/base/Map/PointMarker';
import { dateFormatter } from '../../../utils/formatters';
import { errorNotification } from '../../Notifications/notificationDecorator';

export default function VehiclesSearchMap(props) {
  const { handleOpenDetails } = props;

  const {
    vehicleRegistryList,
    loadingVehicleRegistryList,
    vehicleRegistryListMeta,
    vehicleTrips,
  } = useSelector(getVehicleRegistryData);

  const filterName = 'vehicle-search-registry-filter';
  const defaultFilterValues = {
    vehicle_datetime_at: [
      moment().subtract(1, 'd').format('YYYY-MM-DDTHH:mm:ss'),
      moment().format('YYYY-MM-DDTHH:mm:ss'),
    ],
  };
  const map = useRef(null);
  const [fullScreen, setFullScreen] = useState(false);
  const [bound, setBound] = useState(null);
  const [selected, setSelected] = useState(null);
  const [isOpenFilter, setOpenFilter] = useState(false);
  const dispatch = useDispatch();
  const store = useStore();
  const {
    getValues, watch, control, reset, setValue,
  } = useForm({ defaultValues: getFilterData(filterName, defaultFilterValues) });

  const getBound = () => {
    const last_coordinate = getValues('last_coordinate');
    if (
      map?.current && (
        !last_coordinate
        || !last_coordinate.length
        || Number.isNaN(last_coordinate[0][0]) || !last_coordinate[0][0]
        || Number.isNaN(last_coordinate[0][1]) || !last_coordinate[0][1]
        || Number.isNaN(last_coordinate[1][0]) || !last_coordinate[1][0]
        || Number.isNaN(last_coordinate[1][1]) || !last_coordinate[1][1]
      )
    ) {
      const bound = map?.current.getBounds();
      const southWest = bound.getSouthWest();
      const northEast = bound.getNorthEast();
      return {
        point1: { lat: northEast.lat, lon: northEast.lng },
        point2: { lat: southWest.lat, lon: southWest.lng },
      };
    }
    return {
      point1: { lat: last_coordinate?.[0]?.[0] || 0, lon: last_coordinate?.[0]?.[1] || 0 },
      point2: { lat: last_coordinate?.[1]?.[0] || 0, lon: last_coordinate?.[1]?.[1] || 0 },
    };
  };

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[VEHICLES_REGISTRY_MODULE].vehicleRegistryListMeta, opts);
    const bound = getBound();
    const requestData = {
      ...paginationOpts,
      filter: {
        ...getValues(),
      },
      sort,
    };
    requestData.filter.created_at = [...requestData.filter.vehicle_datetime_at];
    delete requestData.filter.vehicle_datetime_at;

    const p1 = dispatch(loadVehicleRegistryList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };
  const setMap = m => {
    map.current = m;
    loadData();
  };

  const [sort, setSort] = useState(getDefaultSort(vehicleRegistryColumns));
  useEffect(() => {
    const subscription = watch(values => {
      localStorage.setItem(filterName, JSON.stringify(values));
      loadData({ page: 1 });
      initBound(values);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => { initBound(getValues()); }, []);
  useEffect(() => { loadData(); }, [sort]);

  const initBound = values => {
    const last_coordinate = values?.last_coordinate || [];
    if (!Array.isArray(last_coordinate) || !last_coordinate?.length) {
      setBound(null);
    } else {
      setBound(last_coordinate);
    }
  };

  const handleChangeRectangle = polygon => {
    if (
      !polygon
      || !polygon[0][0]
      || !polygon[0][1]
      || !polygon[1][0]
      || !polygon[0][1]
    ) {
      return;
    }
    setValue('last_coordinate', polygon);
  };

  const handleShowTrips = item => {
    setSelected(item);
    // const bound = getBound();
    const requestData = {
      filter: {
        vehicle_id: [item.id],
      },
    };
    const p1 = dispatch(loadVehicleTrips(requestData)).catch(response => errorNotification('Загрузка данных', getResponseError(response)));
    // .catch(responseLogWithNotification);
  };

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  const renderContextMenu = item => (
    <div className="more-wrapper">
      <Button variant="link" className="w-100 more-button" onClick={() => handleOpenDetails(item)}>
        Посмотреть
      </Button>
    </div>
  );

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={renderContextMenu(item)}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  return (
    <>
      <VehicleFilterRow
        selectedTab={3}
        toggleFilter={() => setOpenFilter(!isOpenFilter)}
        control={control}
        setValue={setValue}
        getValues={getValues}
      />
      <div className={classNames('map-columns card search-map-columns', { 'map-fullscreen': fullScreen })}>
        <Loader global loading={loadingVehicleRegistryList} />
        <div className="search-table">
          <div className="grid-wrapper">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                data={vehicleRegistryList}
                columns={vehicleMapSearchColumns}
                name="vehicle-serarch-list"
                loading={loadingVehicleRegistryList}
                additionalColumns={[]}
                changeSort={changeSort}
                sort={sort}
                onDoubleClickByRow={handleShowTrips}
                settingsFormatter={moreFormatter}
                renderContextMenu={renderContextMenu}
                rowClassFormatter={i => (i.id === selected?.id ? 'clickable-table-row selected' : 'clickable-table-row')}
              />
            </div>
          </div>
          <Pagination meta={vehicleRegistryListMeta} list={vehicleRegistryList} handleChangeMeta={loadData} />
        </div>
        <div className="map-box">
          <div className="map-wrapper">
            <LeafletMap classNames="stops-map" setMap={setMap} onMove={loadData} onMoveEnd={loadData}>
              {vehicleTrips && vehicleTrips.length > 0 ? vehicleTrips.map((trip, key) => {
                const lat = Number(trip.vehicle_latitude);
                const lon = Number(trip.vehicle_longitude);
                if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
                const coords = [lat, lon];
                return (
                  <PointMarker
                    key={`circle-${key}`}
                    center={coords}
                    text={dateFormatter(trip.vehicle_datetime_at)}
                  />
                );
              }) : null}

              <FullScreenControl onClick={() => setFullScreen(!fullScreen)} fullScreen={fullScreen} />
              <RectangleDrawer onChange={handleChangeRectangle} getValues={getValues} bound={bound} watch={watch} />
            </LeafletMap>
          </div>
        </div>
        {isOpenFilter
          ? (
            <FilterModal
              control={control}
              onClose={() => setOpenFilter(false)}
              getValues={getValues}
              reset={reset}
              defaultValues={defaultFilterValues}
              page={PAGE_TYPE.VEHICLE_REGISTRY}
            />
          )
          : null}
      </div>
    </>

  );
}
